import React, { useEffect, useId, useRef, useState } from 'react';
import { PriceLine } from '../../services/summaryPriceCalculations';
import { AppliedDiscount } from '../../hooks/useDiscountCode';
import PriceLineRow from '../UI/PriceLineRow';
import { taxNote } from '../../utils/priceFormat';

export interface ContactFormData {
  type: 'private' | 'business';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  companyName?: string;
  position?: string;
  vatId?: string;
  technicalDrawing: boolean;
  privacyAccepted: boolean;
}

interface OfferFormProps {
  formData: ContactFormData;
  onFormChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTypeChange: (type: 'private' | 'business') => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  isSubmitting: boolean;
  offerCreated: boolean;
  priceLines: PriceLine[];
  finalPrice: number;
  showGross: boolean;
  formatPrice: (price: number) => string;
  formatLineAmount: (net: number | null) => string;
  discountCode: string;
  onDiscountCodeChange: (code: string) => void;
  appliedDiscount: AppliedDiscount | null;
  discountError: string;
  isValidatingDiscount: boolean;
  onValidateDiscount: (code: string) => void;
  onRemoveDiscount: () => void;
  discountAmount: number;
}

function FormRow({
  label,
  name,
  value,
  onChange,
  type = 'text',
  required = false,
  autoComplete,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  const id = `sv-offer-${name}`;
  return (
    <div className="sv-offer-row">
      <label htmlFor={id} className="sv-offer-row-label">
        {label}
        {required && <span className="text-rubric ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="sv-offer-row-input"
      />
    </div>
  );
}

function OfferHint({ title }: { title: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const noteId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="sv-offer-hint" ref={wrapRef}>
      <div className="sv-offer-title">
        {title}
        <button
          type="button"
          className="sv-info-btn"
          aria-expanded={open}
          aria-controls={noteId}
          aria-label="Hinweis zum Angebot"
          onClick={() => setOpen((value) => !value)}
        >
          <svg
            viewBox="0 0 16 16"
            width="18"
            height="18"
            aria-hidden="true"
            className="sv-info-mark"
          >
            <rect
              x="0.5"
              y="0.5"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <rect x="7.25" y="3.4" width="1.5" height="1.5" fill="currentColor" />
            <path
              d="M8 7.1v5.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </button>
      </div>
      {open && (
        <p id={noteId} role="note" className="sv-offer-hint-copy">
          Wir senden das Angebot als PDF per E-Mail. Unverbindlich — es
          entsteht keine Bestellung.
        </p>
      )}
    </div>
  );
}

/**
 * Kontaktblatt der Zusammenfassung. Felder stehen in derselben
 * Zeilengrammatik wie die Preisliste; der Hinweis sitzt hinter dem i.
 */
export default function OfferForm({
  formData,
  onFormChange,
  onCheckboxChange,
  onTypeChange,
  onSubmit,
  onBack,
  isSubmitting,
  offerCreated,
  priceLines,
  finalPrice,
  showGross,
  formatPrice,
  formatLineAmount,
  discountCode,
  onDiscountCodeChange,
  appliedDiscount,
  discountError,
  isValidatingDiscount,
  onValidateDiscount,
  onRemoveDiscount,
  discountAmount,
}: OfferFormProps) {
  const plzId = 'sv-offer-postalCode';
  const cityId = 'sv-offer-city';

  return (
    <div className="sv-offer">
      <section>
        <header className="sv-offer-head">
          <OfferHint
            title={
              <h2 className="sv-h2 text-ink">Wohin senden wir das Angebot?</h2>
            }
          />
          <button type="button" onClick={onBack} className="sv-offer-back">
            ← Übersicht
          </button>
        </header>

        <form onSubmit={onSubmit} className="sv-offer-form">
          <div
            className={`sv-tax-switch${formData.type === 'business' ? ' is-gross' : ''}`}
            role="radiogroup"
            aria-label="Kundentyp"
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' || event.key === 'Home') {
                event.preventDefault();
                onTypeChange('private');
              }
              if (event.key === 'ArrowRight' || event.key === 'End') {
                event.preventDefault();
                onTypeChange('business');
              }
            }}
          >
            <span className="sv-tax-switch-thumb" aria-hidden="true" />
            <button
              type="button"
              role="radio"
              aria-checked={formData.type === 'private'}
              className="sv-tax-switch-btn"
              onClick={() => onTypeChange('private')}
            >
              Privat
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={formData.type === 'business'}
              className="sv-tax-switch-btn"
              onClick={() => onTypeChange('business')}
            >
              Firma
            </button>
          </div>

          <div className="sv-offer-sheet">
            {formData.type === 'business' && (
              <>
                <FormRow
                  label="Firmenname"
                  name="companyName"
                  value={formData.companyName || ''}
                  onChange={onFormChange}
                  required
                  autoComplete="organization"
                />
                <FormRow
                  label="Position"
                  name="position"
                  value={formData.position || ''}
                  onChange={onFormChange}
                  required
                  autoComplete="organization-title"
                />
                <FormRow
                  label="USt-IdNr."
                  name="vatId"
                  value={formData.vatId || ''}
                  onChange={onFormChange}
                  placeholder="DE…"
                />
              </>
            )}

            <FormRow
              label="Vorname"
              name="firstName"
              value={formData.firstName}
              onChange={onFormChange}
              required
              autoComplete="given-name"
            />
            <FormRow
              label="Nachname"
              name="lastName"
              value={formData.lastName}
              onChange={onFormChange}
              required
              autoComplete="family-name"
            />
            <FormRow
              label="E-Mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={onFormChange}
              required
              autoComplete="email"
            />
            <FormRow
              label="Telefon"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={onFormChange}
              required
              autoComplete="tel"
            />
            <FormRow
              label="Straße"
              name="street"
              value={formData.street}
              onChange={onFormChange}
              required
              autoComplete="street-address"
            />
            <div className="sv-offer-row">
              <span className="sv-offer-row-label" id="sv-offer-place-label">
                PLZ / Ort
                <span className="text-rubric ml-1">*</span>
              </span>
              <div
                className="sv-offer-place"
                role="group"
                aria-labelledby="sv-offer-place-label"
              >
                <input
                  id={plzId}
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={onFormChange}
                  required
                  autoComplete="postal-code"
                  aria-label="Postleitzahl"
                  className="sv-offer-row-input is-plz"
                />
                <input
                  id={cityId}
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={onFormChange}
                  required
                  autoComplete="address-level2"
                  aria-label="Ort"
                  className="sv-offer-row-input"
                />
              </div>
            </div>
          </div>

          <div className="sv-offer-extras">
            {!appliedDiscount ? (
              <div>
                <div className="sv-offer-row is-plain">
                  <label
                    htmlFor="sv-offer-discount"
                    className="sv-offer-row-label"
                  >
                    Rabattcode
                  </label>
                  <div className="sv-offer-discount">
                    <input
                      id="sv-offer-discount"
                      type="text"
                      placeholder="Code"
                      value={discountCode}
                      onChange={(e) =>
                        onDiscountCodeChange(e.target.value.toUpperCase())
                      }
                      className="sv-offer-row-input is-code"
                    />
                    <button
                      type="button"
                      onClick={() => onValidateDiscount(discountCode)}
                      disabled={isValidatingDiscount || !discountCode.trim()}
                      className="sv-offer-inline"
                    >
                      {isValidatingDiscount ? 'Prüfe …' : 'Einlösen'}
                    </button>
                  </div>
                </div>
                {discountError && (
                  <p className="sv-offer-error">{discountError}</p>
                )}
              </div>
            ) : (
              <div className="sv-offer-row is-plain">
                <p className="sv-offer-row-label text-rubric">
                  {appliedDiscount.code}
                </p>
                <div className="sv-offer-discount">
                  <p className="sv-offer-discount-note">
                    {appliedDiscount.type === 'percentage'
                      ? `${appliedDiscount.value} % Rabatt`
                      : `${formatPrice(appliedDiscount.value)} Rabatt`}
                    {' — '}
                    <span className="sv-mono text-ink tabular-nums">
                      {formatPrice(discountAmount)}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={onRemoveDiscount}
                    className="sv-offer-inline"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            )}

            <label className="sv-offer-privacy">
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={onCheckboxChange}
                required
                className="sv-offer-check"
              />
              <span>
                Ich akzeptiere die{' '}
                <a href="#">Datenschutzerklärung</a> und stimme der
                Verarbeitung meiner Daten zu.
                <span className="text-rubric ml-1">*</span>
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || offerCreated}
            className={`sv-btn sv-btn-primary ${
              isSubmitting || offerCreated ? 'is-disabled' : ''
            }`}
          >
            {isSubmitting
              ? 'Angebot wird erstellt …'
              : offerCreated
                ? 'Angebot bereits erstellt'
                : 'Unverbindliches Angebot'}
          </button>
        </form>
      </section>

      <aside className="sv-offer-panel lg:sticky lg:top-6">
        <p className="sv-offer-price">{formatPrice(finalPrice)}</p>
        <p className="sv-offer-note">{taxNote(showGross)}</p>
        <dl className="sv-spec mt-6">
          {priceLines.map((line) => (
            <PriceLineRow
              key={line.id}
              label={line.label}
              detail={line.detail}
              amount={
                line.net != null && line.net > 0
                  ? formatLineAmount(line.net)
                  : 'inklusive'
              }
              included={!(line.net != null && line.net > 0)}
              compact
            />
          ))}
        </dl>
      </aside>
    </div>
  );
}
